/**
 * Notification Sound Utility
 *
 * Plays notification sound using HTML5 Audio.
 * Set USE_AUDIO_FILE to false to use Web Audio API generated sounds instead.
 */

const USE_AUDIO_FILE = true; // Set to false to use programmatic Web Audio API sounds

class NotificationSound {
  private audioElement: HTMLAudioElement | null = null;
  private audioContext: AudioContext | null = null;
  private isInitialized = false;

  constructor() {
    if (typeof window !== 'undefined') {
      this.init();
    }
  }

  private init() {
    if (USE_AUDIO_FILE) {
      // Use audio file from public folder
      try {
        this.audioElement = new Audio('/notification.wav');
        this.audioElement.volume = 0.5;
        this.audioElement.preload = 'auto';
        this.isInitialized = true;

        // Setup user interaction listeners to unlock audio playback
        this.setupAudioUnlock();
      } catch (error) {
        console.warn('Could not load notification audio file:', error);
        this.isInitialized = false;
      }
    } else {
      // Use Web Audio API for programmatic sounds
      try {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        this.isInitialized = true;
        this.setupUserInteractionListeners();
      } catch (error) {
        console.warn('Web Audio API not supported:', error);
        this.isInitialized = false;
      }
    }
  }

  private setupAudioUnlock() {
    // For HTML5 Audio, we need to unlock it with user interaction
    const unlockAudio = async () => {
      if (this.audioElement) {
        try {
          // Play and immediately pause to unlock audio
          const playPromise = this.audioElement.play();
          if (playPromise !== undefined) {
            await playPromise;
            this.audioElement.pause();
            this.audioElement.currentTime = 0;
            console.log('[NotificationSound] Audio unlocked successfully');
          }
        } catch (error) {
          console.warn('[NotificationSound] Failed to unlock audio:', error);
        }
      }
      // Remove listeners after first interaction
      ['click', 'touchstart', 'keydown'].forEach(eventType => {
        document.removeEventListener(eventType, unlockAudio);
      });
    };

    // Listen for any user interaction
    ['click', 'touchstart', 'keydown'].forEach(eventType => {
      document.addEventListener(eventType, unlockAudio, { once: true, passive: true });
    });
  }

  private setupUserInteractionListeners() {
    // Track user interaction to enable autoplay (only needed for Web Audio API)
    const markInteracted = () => {
      if (this.audioContext && this.audioContext.state === 'suspended') {
        this.audioContext.resume().catch(err => {
          console.warn('Could not resume audio context:', err);
        });
      }
    };

    ['click', 'touchstart', 'keydown'].forEach(eventType => {
      document.addEventListener(eventType, markInteracted, { once: true, passive: true });
    });
  }

  /**
   * Play notification sound
   */
  public async play(): Promise<void> {
    console.log('[NotificationSound] play() called, initialized:', this.isInitialized);

    if (!this.isInitialized) {
      console.warn('[NotificationSound] Not initialized');
      return;
    }

    try {
      if (USE_AUDIO_FILE && this.audioElement) {
        console.log('[NotificationSound] Playing audio file...');
        // Play audio file
        this.audioElement.currentTime = 0; // Reset to start
        const playPromise = this.audioElement.play();

        if (playPromise !== undefined) {
          await playPromise;
          console.log('[NotificationSound] Audio played successfully');
        }
      } else if (!USE_AUDIO_FILE && this.audioContext) {
        console.log('[NotificationSound] Playing programmatic sound...');
        // Generate programmatic sound
        await this.playProgrammaticSound();
      }
    } catch (error) {
      console.error('[NotificationSound] Failed to play sound:', error);
    }
  }

  /**
   * Play a pleasant two-tone notification sound using Web Audio API
   * First tone: 800Hz, Second tone: 600Hz
   */
  private async playProgrammaticSound(): Promise<void> {
    if (!this.audioContext) return;

    try {
      // Resume context if suspended (browser autoplay policy)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      const now = this.audioContext.currentTime;

      // Create oscillator for first tone (800Hz)
      const oscillator1 = this.audioContext.createOscillator();
      const gainNode1 = this.audioContext.createGain();

      oscillator1.connect(gainNode1);
      gainNode1.connect(this.audioContext.destination);

      oscillator1.frequency.value = 800; // High tone
      oscillator1.type = 'sine';

      // First tone: quick fade in, then fade out
      gainNode1.gain.setValueAtTime(0, now);
      gainNode1.gain.linearRampToValueAtTime(0.3, now + 0.01);
      gainNode1.gain.linearRampToValueAtTime(0, now + 0.1);

      oscillator1.start(now);
      oscillator1.stop(now + 0.1);

      // Create oscillator for second tone (600Hz)
      const oscillator2 = this.audioContext.createOscillator();
      const gainNode2 = this.audioContext.createGain();

      oscillator2.connect(gainNode2);
      gainNode2.connect(this.audioContext.destination);

      oscillator2.frequency.value = 600; // Lower tone
      oscillator2.type = 'sine';

      const secondToneStart = now + 0.1;
      gainNode2.gain.setValueAtTime(0, secondToneStart);
      gainNode2.gain.linearRampToValueAtTime(0.3, secondToneStart + 0.01);
      gainNode2.gain.linearRampToValueAtTime(0, secondToneStart + 0.15);

      oscillator2.start(secondToneStart);
      oscillator2.stop(secondToneStart + 0.15);
    } catch (error) {
      console.warn('Could not play programmatic sound:', error);
    }
  }

  /**
   * Clean up resources
   */
  public dispose() {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement = null;
    }
    if (this.audioContext) {
      this.audioContext.close().catch(err => {
        console.warn('Error closing audio context:', err);
      });
      this.audioContext = null;
    }
    this.isInitialized = false;
  }
}

// Create singleton instance
const notificationSound = new NotificationSound();

/**
 * Play notification sound
 * Safe to call multiple times - uses singleton instance
 */
export const playNotificationSound = () => {
  // Sound disabled — visual notifications only
};

/**
 * Clean up notification sound resources
 * Call this when the app is unmounting if needed
 */
export const disposeNotificationSound = () => {
  notificationSound.dispose();
};
